import React, {
  useCallback,
  useEffect,
  useState,
} from "react";


import FilterPanel from "./FilterPanel";
import OverviewCards from "./OverviewCards";
import ActionRequired from "./ActionRequired";
import CustomerSummary from "./CustomerSummary";
import SubscriptionSummary from "./SubscriptionSummary";
import RenewalSummary from "./RenewalSummary";
import TrialSummary from "./TrialSummary";
import VerificationSummary from "./VerificationSummary";
import UpcomingStarts from "./UpcomingStarts";
import PaymentSummary from "./PaymentSummary";
import DeliverySummary from "./DeliverySummary";
import BranchPerformance from "./BranchPerformance";
import { DashboardFilters, DashboardResponse, fetchDashboard } from "./dashboardType";

const DEFAULT_FILTERS: DashboardFilters = {
  period: "today",
};

interface DashboardContainerProps {
  /** Pass through from your auth context if some filters are super-user-only. */
  isSuperUser?: boolean;
}

export default function DashboardContainer({
  isSuperUser = false,
}: DashboardContainerProps) {
  const [filters, setFilters] =
    useState<DashboardFilters>(DEFAULT_FILTERS);

  const [dashboard, setDashboard] =
    useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadDashboard = useCallback(
    async (activeFilters: DashboardFilters) => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchDashboard(activeFilters);
        setDashboard(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load dashboard data."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (filters.period === "custom" && !filters.start_date) return;
    loadDashboard(filters);
  }, [filters]);

  const updateFilter = (key: keyof DashboardFilters, value: any) => {
  const next = { ...filters, [key]: value };       // ⚠️ built from stale `filters`
  setFilters(prev => ({ ...prev, [key]: value }));  // ✅ correctly merged into state
  if (!(key === "period" && value === "custom")) {
    loadDashboard(next);                            // ⚠️ but fetch uses the stale `next`
  }
};

  // Now takes the dates directly instead of reading lifted state.
  const applyCustomDate = (start: string, end: string) => {
    if (!start || !end) return;

    const next: DashboardFilters = {
      ...filters,
      period: "custom",
      start_date: start,
      end_date: end,
    };

    setFilters(next);
    loadDashboard(next);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    loadDashboard(DEFAULT_FILTERS);
  };

  return (
    <div className="mx-auto max-w-full space-y-6 p-6">
      <FilterPanel
        filters={filters}
        updateFilter={updateFilter}
        applyCustomDate={applyCustomDate}
        clearFilters={clearFilters}
        isSuperUser={isSuperUser}
      />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !dashboard && (
        <div className="rounded-xl border bg-white p-8 text-center text-gray-500 shadow-sm">
          Loading dashboard...
        </div>
      )}

      {dashboard && (
        <div className={loading ? "opacity-60 transition-opacity" : ""}>
          <div className="space-y-6">
            <OverviewCards data={dashboard.overview} />
            <ActionRequired data={dashboard.action_required} />
            <CustomerSummary data={dashboard.customers} />
            <SubscriptionSummary data={dashboard.subscriptions} />
            <RenewalSummary data={dashboard.renewals} />
            <TrialSummary data={dashboard.trials} />
            <VerificationSummary data={dashboard.verification} />
            <UpcomingStarts data={dashboard.upcoming_starts} />
            <PaymentSummary data={dashboard.payments} />
            <DeliverySummary data={dashboard.delivery} />
            <BranchPerformance data={dashboard.branch_performance} />
          </div>
        </div>
      )}
    </div>
  );
}